import { useRecoilCallback } from "recoil";
import { folderTreeState } from "../store/folderTree.store";
import { FolderNode, FolderTree } from "../../api-client-nswag/custompermission-client";
import { usersPerSiteSelector } from "../selectors/legalEntities.selectors";

export const useFolderTreeActions = () => {
  const loadFolderTree = useRecoilCallback(
    ({ set }) =>
      (folderTree: any) => {
        set(folderTreeState, folderTree);
      },
    [],
  );

  const updateFolderNode = useRecoilCallback(
    ({ set }) =>
      (
        folderId: number,
        updatedUsers: string[]
      ) => {
        set(folderTreeState, (prev: FolderTree) => {
          if (!Array.isArray(prev)) return prev;

          const removeFromSubtree = (
            node: FolderNode,
            toRemove: Set<string>
          ): FolderNode => {
            const nextUsers = node.users.filter((u) => !toRemove.has(u));

            return {
              ...node,
              users: nextUsers,
              usersCount: nextUsers?.length,
              children: node.children.map((child) =>
                removeFromSubtree(child, toRemove)
              ),
            };
          };

          const walk = (node: FolderNode): FolderNode => {
            if (node.folderId === folderId) {
              const previousUsers = node.users ?? [];
              const updatedSet = new Set(updatedUsers);

              const removed = previousUsers.filter((u) => !updatedSet.has(u));
              const toRemove = new Set(removed);

              const updatedNode: FolderNode = {
                ...node,
                users: updatedUsers,
                usersCount: updatedUsers?.length,
              };

              if (toRemove.size === 0) {
                return {
                  ...updatedNode,
                  children: node.children.map(walk),
                };
              }

              return removeFromSubtree(updatedNode, toRemove);
            }

            return {
              ...node,
              children: node.children.map(walk),
            };
          };

          return prev.map(walk);
        });
      },
    []
  );

  const setFolderTreeToAllUsers = useRecoilCallback(
    ({ set }) =>
      (allUsers: string[]) => {
        set(folderTreeState, (prev: FolderTree) => {
          if (!Array.isArray(prev)) return prev;

          const visit = (node: FolderNode): FolderNode => ({
            ...node,
            users: [...allUsers],
            usersCount: allUsers.length,
            children: (node.children ?? []).map(visit),
          });

          return prev.map(visit);
        });
      },
    []
  );

  const revalidateFolderIsRestrictedByParent = useRecoilCallback(
    ({ set, snapshot }) =>
      async ({ targetLevel = 1 }: { targetLevel?: number }) => {
        const tree = await snapshot.getPromise(folderTreeState);
        const usersPerSite = await snapshot.getPromise(
          usersPerSiteSelector
        );

        if (!tree?.length) return;

        const siteUsers: string[] = (usersPerSite ?? []).map(user =>
          String(user.userId)
        );

        const siteUserSet = new Set(siteUsers);

        const isCustomFolder = (node: FolderNode): boolean =>
          Number(node.entityId) === 0;

        const hasAllSiteUsers = (
          users: Array<string | number> = []
        ): boolean => {
          const folderUserSet = new Set(users.map(String));

          return (
            folderUserSet.size === siteUserSet.size &&
            siteUsers.every(userId =>
              folderUserSet.has(userId)
            )
          );
        };

        /*
         * ENTITY-FOLDER LOGIC
         *
         * This preserves the original entity-folder behavior by checking
         * the complete subtree, including custom folders belonging to it.
         */
        const subtreeHasInvalid = (
          node: FolderNode
        ): boolean => {
          if (!hasAllSiteUsers(node.users)) {
            return true;
          }

          return (node.children ?? []).some(child =>
            subtreeHasInvalid(child)
          );
        };

        /*
         * ENTITY-FOLDER PROPAGATION
         *
         * Preserves the existing entity-folder behavior by propagating the
         * calculated entity restriction through that entity subtree.
         */
        const setSubtreeRestriction = (
          node: FolderNode,
          isRestricted: boolean
        ): FolderNode => ({
          ...node,
          isRestricted,
          children: (node.children ?? []).map(child =>
            setSubtreeRestriction(child, isRestricted)
          ),
        });

        /*
         * CUSTOM-FOLDER LOGIC
         *
         * A custom folder is restricted when:
         * 1. Its parent custom folder is restricted, or
         * 2. The custom folder itself does not contain all site users.
         *
         * Only custom-folder descendants use this propagation rule.
         * Entity-folder descendants retain their existing values.
         */
        const updateCustomFolders = (
          nodes: FolderNode[],
          parentRestricted: boolean
        ): FolderNode[] =>
          nodes.map(node => {
            if (!isCustomFolder(node)) {
              return node;
            }

            const currentFolderInvalid =
              !hasAllSiteUsers(node.users);

            const isRestricted =
              parentRestricted || currentFolderInvalid;

            return {
              ...node,
              isRestricted,
              children: updateCustomFolders(
                node.children ?? [],
                isRestricted
              ),
            };
          });

        const process = (
          nodes: FolderNode[],
          level: number,
          parentCustomRestricted = false
        ): FolderNode[] =>
          nodes.map(node => {
            /*
             * ENTITY FOLDER
             *
             * Apply the existing entity subtree logic only when the node is
             * an entity folder at the requested level.
             */
            if (
              level === targetLevel &&
              !isCustomFolder(node)
            ) {
              const invalid = subtreeHasInvalid(node);

              return setSubtreeRestriction(
                node,
                invalid
              );
            }

            /*
             * CUSTOM FOLDER
             *
             * Once the requested custom-folder level is reached, apply the
             * custom parent restriction logic to that custom folder and its
             * custom-folder descendants.
             */
            if (
              level === targetLevel &&
              isCustomFolder(node)
            ) {
              const currentFolderInvalid =
                !hasAllSiteUsers(node.users);

              const isRestricted =
                parentCustomRestricted ||
                currentFolderInvalid;

              return {
                ...node,
                isRestricted,
                children: updateCustomFolders(
                  node.children ?? [],
                  isRestricted
                ),
              };
            }

            /*
             * Continue looking for folders at targetLevel without changing
             * folders above that level.
             */
            return {
              ...node,
              children: process(
                node.children ?? [],
                level + 1,
                isCustomFolder(node)
                  ? node.isRestricted
                  : false
              ),
            };
          });

        /*
         * Preserve the existing explicitly requested root-level entity logic.
         */
        if (targetLevel === 0) {
          const nextTree = tree.map(root => {
            /*
             * A custom root uses the custom-folder parent propagation rule.
             */
            if (isCustomFolder(root)) {
              const currentFolderInvalid =
                !hasAllSiteUsers(root.users);

              return {
                ...root,
                isRestricted: currentFolderInvalid,
                children: updateCustomFolders(
                  root.children ?? [],
                  currentFolderInvalid
                ),
              };
            }

            /*
             * An entity root retains the existing subtree validation and
             * subtree propagation behavior.
             */
            const level1Children =
              root.children ?? [];

            const anyInvalidUnderRoot =
              level1Children.some(child =>
                subtreeHasInvalid(child)
              );

            return {
              ...root,
              isRestricted: anyInvalidUnderRoot,
              children: level1Children.map(child =>
                setSubtreeRestriction(
                  child,
                  anyInvalidUnderRoot
                )
              ),
            };
          });

          set(folderTreeState, nextTree);
          return;
        }

        const nextTree = process(tree, 0);

        set(folderTreeState, nextTree);
      },
    []
  );
  return {
    loadFolderTree,
    updateFolderNode,
    setFolderTreeToAllUsers,
    revalidateFolderIsRestrictedByParent
  };
};
